<template>
  <div>
    <VCard :title="$t('users')">
      <template v-if="$can('users.create')" #header-left>
        <VButton
          :disabled="$inSafeGhostMode || $organization.canInviteUser.disabled"
          :help="$organization.canInviteUser.help"
          icon="Add"
          size="small"
          @click="showCreateUserDialog"
        >
          {{ $t('invite-user') }}
        </VButton>
        <VButton
          :disabled="$inSafeGhostMode || $organization.canInviteUser.disabled"
          :help="$organization.canInviteUser.help"
          icon="Add"
          size="small"
          @click="dialogImportUsersVisible = true"
        >
          {{ $t('import-users') }}
        </VButton>
      </template>

      <template #header-right>
        <div class="flex-center">
          <VSelect v-model="columns.selected" multiple size="small" :sort="columns.sorted" style="min-width: 200px; margin-right: 10px">
            <template #value>{{ $t('columns') }}</template>

            <VOption
              v-for="(column, index) in columns.sorted.filter((column) => $can('users.update') || column !== 'one_time_password_required')"
              :key="column"
              :index
              :label="columnLabels[column]"
              :value="column"
            />
          </VSelect>
          <FilterButton v-model="filtersVisible" :active="filtersActive" />
        </div>
      </template>

      <VTransitionCollapse lazy :visible="filtersVisible">
        <VCard>
          <VGrid class="mb-5" :gap="20" stretch>
            <VCol :md="8">
              <VInput v-model="filters.query" clearable :label="$t('search')" size="small" />
            </VCol>

            <VCol :md="8">
              <VSelect v-model="filters.group_id" clearable filterable :label="$t('group')" multiple size="small">
                <VOption v-for="group in sortedGroups" :key="group.id" :label="group.name" :value="group.id" />
              </VSelect>
            </VCol>

            <VCol :md="8" style="display: flex; align-items: center">
              <VSwitch v-model="filters.deleted" class="mt-7" :label="$t('deleted')" />
            </VCol>
          </VGrid>

          <VButton size="small" type="link" @click="filters.reset()">{{ $t('reset-filters') }}</VButton>
        </VCard>
      </VTransitionCollapse>

      <UsersTable
        :columns="['first_name', 'email', ...columns.selected.filter((column) => $can('users.update') || column !== 'one_time_password_required'), 'actions']"
        :data="filteredUsers"
      >
        <template #actions="{ row }: { row: User }">
          <template v-if="!filters.deleted">
            <VButton :disabled="$inSafeGhostMode" icon="Edit" type="link" @click="editUser(row)" />

            <VTooltip placement="top" tag="span" wrapper-tag="span">
              <template #content>{{ $t('delete') }}</template>
              <VButton
                :disabled="$inSafeGhostMode"
                :disabled-help="{ [$t('your-organization-must-have-at-least-one-owner')]: row.id === onlyOwnerId }"
                icon="Bin"
                type="link"
                @click="userToRemove = row"
              />
            </VTooltip>
          </template>

          <template v-else>
            <VButton :disabled="$inSafeGhostMode" icon="Bin" type="link" @click="forceRemove(row.id)" />
            <VButton :disabled="$inSafeGhostMode" icon="Reset" type="link" @click="restore(row.id)" />
          </template>
        </template>
      </UsersTable>

      <DialogUserCreate v-model:visible="dialogCreateVisible" :create />
      <DialogUserEdit v-model:visible="dialogEditVisible" :user="selectedUser" />
      <DialogUserRemove v-if="userToRemove" :user="userToRemove" :visible="!!userToRemove" @update:visible="userToRemove = undefined" />
    </VCard>

    <VCard id="user-invitations" :title="$t('user-invitations')">
      <VTable :data="userInvitations" :default-sort="{ prop: 'expires_at', order: 'asc' }" :pagination="{}" row-key="id">
        <VTableColumn
          v-slot="{ row }: { row: UserInvitation }"
          cell-class="flex-center"
          :label="$t('name')"
          max-width="200"
          prop="user.first_name"
          show-overflow-tooltip
          sortable
          :tooltip-content="(row: UserInvitation) => row.user.name"
        >
          <UserInvitationAvatar :size="26" style="margin-right: 10px" :user="row.user" />
          {{ row.user.name }}
        </VTableColumn>

        <VTableColumn :label="$t('email')" max-width="150" prop="user.email" show-overflow-tooltip sortable />

        <VTableColumn v-slot="{ row }: { row: UserInvitation }" :label="$t('groups')" max-width="200" show-overflow-tooltip>
          <template v-if="row.group_ids?.length">
            <VTag v-for="groupId in row.group_ids.filter((groupId) => $groups[groupId])" :key="groupId" type="primary">{{ $groups[groupId].name }}</VTag>
          </template>
        </VTableColumn>

        <VTableColumn v-slot="{ row }: { row: UserInvitation }" :label="$t('roles')" max-width="200" show-overflow-tooltip>
          <template v-if="row.role_names?.length">
            {{
              row.role_names
                .filter((name) => isAppRole(name) && !isAppModuleRole(name))
                .map((role) => $tk(role))
                .join(', ')
            }}
          </template>
        </VTableColumn>

        <VTableColumn v-slot="{ row }: { row: UserInvitation }" :label="$t('expires-at')" max-width="150" prop="expires_at" sortable>
          <FieldDate class="text-neutral-400" relative :value="row.expires_at" />
        </VTableColumn>

        <VTableColumn v-if="$can('users.update')" v-slot="{ row }: { row: UserInvitation }" :label="$t('actions')">
          <VTooltip placement="top" tag="span" wrapper-tag="span">
            <template #content>{{ $t('delete') }}</template>
            <VButton :disabled="$inSafeGhostMode" icon="Bin" type="link" @click="removeInvitation(row)" />
          </VTooltip>
        </VTableColumn>
      </VTable>
    </VCard>

    <DialogImportUsers v-if="$can('users.create')" v-model:visible="dialogImportUsersVisible" />
  </div>
</template>

<script setup lang="ts">
import { IndexedDBCache } from '~/services/cache/IndexedDBCache.ts'
import { isAppModuleRole, isAppRole } from '~/stores/roles.ts'
import { type User, type UserInvitation } from '~/types.ts'
import type { DialogUserCreateForm } from './dialogs/DialogUserCreate.vue'

const columnLabels: Record<string, string> = {
  function: $t('function'),
  groups: $t('groups'),
  roles: $t('roles'),
  modules: $t('modules'),
  one_time_password_required: $t('2FA'),
}

const { filters: columns } = useFilters(
  {
    sorted: ['function', 'groups', 'roles', 'modules', 'one_time_password_required'],
    selected: ['function', 'groups', 'roles'],
  },
  {
    cache: IndexedDBCache.tags('Users'),
    cacheKey: 'columns.v2',
  },
)

export type UserFilters = ReturnType<typeof filters.toJson>

const { filters, filtersActive } = useFilters({
  query: '',
  group_id: [] as number[],
  deleted: false,
})

const filtersVisible = ref(false)
const dialogCreateVisible = ref(false)
const dialogEditVisible = ref(false)
const dialogImportUsersVisible = ref(false)
const selectedUser = ref<User>()
const userToRemove = ref<User>()
const userInvitations = ref<UserInvitation[]>([])

const onlyOwnerId = computed(() => {
  const owners = Object.values(stores.users.users).filter((user) => user.roles.some((role) => role.name === 'Owner'))

  return owners.length === 1 ? owners[0].id : undefined
})

const sortedGroups = computed(() => {
  return Object.values(stores.groups.groups).sort((a, b) => {
    if (a.id === null) return -1
    if (b.id === null) return 1

    return a.name.localeCompare(b.name)
  })
})

const filteredUsers = computed(() => {
  let users = Object.values(stores.users.users)

  if (filters.deleted) {
    users = Object.values(stores.users.deletedUsers)
  }

  if (filters.group_id.length > 0) {
    users = users.filter((user) => user.groups.some((group) => filters.group_id.includes(group.id)))
  }

  if (filters.query.length > 0) {
    users = filterArrayByObjectValues({ array: users, query: filters.query, keys: ['first_name', 'last_name', 'email'], customFilter })
  }

  return users
})

const bus = useBus()

bus.on('users.filter', (newFilters) => {
  Object.assign(filters, newFilters)
  filtersVisible.value = true
})

onActivated(() => {
  fetchInvitations()
})

function showCreateUserDialog() {
  dialogCreateVisible.value = true
  stores.apmEvents.track({ event: 'add-user', category: 'users' })
}

function customFilter(user: User) {
  const name = `${user.first_name} ${user.last_name}`
  if (name.toLowerCase().includes(filters.query.toLowerCase())) {
    return true
  }

  if (user.groups.length === 0) {
    return false
  }

  return user.groups.some((group) => group.name.toLowerCase().includes(filters.query.toLowerCase()))
}

async function create(form: DialogUserCreateForm) {
  const { roles, module_roles: moduleRoles, ...rest } = form
  await api.post<User>('user-invitations', {
    ...rest,
    roles: [...roles, ...moduleRoles],
  })
  fetchInvitations()
}

async function removeInvitation(invitation: UserInvitation) {
  await $confirm({
    title: $t('delete-invitation'),
    message: $t('if-you-proceed-with-this-action-the-user-invitation-for-email-will-be-deleted', { email: invitation.user.email }),
    confirmButtonType: 'error',
  })

  try {
    await api.delete<User>(`user-invitations/${invitation.id}`)
    $message.success($t('the-user-invitation-has-been-successfully-removed'))
  } catch {
    $message.error($t('api-error'))
  }

  fetchInvitations()
}

async function forceRemove(id: number) {
  const user = stores.users.deletedUsers[id]
  await $confirm({
    title: $t('delete-user'),
    message: $t('if-you-proceed-with-this-action-the-user-user-will-be-permanently-deleted', { email: user.email }),
    confirmButtonType: 'error',
  })
  await stores.users.forceDelete(id)
}

async function restore(id: number) {
  const user = stores.users.deletedUsers[id]
  await $confirm({ title: $t('restore-user'), message: $t('if-you-proceed-with-this-action-the-user-user-will-be-restored', { email: user.email }) })
  await stores.users.restore(id)
}

function editUser(user: User) {
  selectedUser.value = user
  dialogEditVisible.value = true
}

async function fetchInvitations() {
  const { data } = await api.post('user-invitations/searches', {
    organization_ids: [stores.organization.id],
    expires_at: [dayjs(), dayjs().add(1, 'year')],
  })
  userInvitations.value = data
}
</script>
